import type { RouteRecordRaw } from "vue-router";
import { takeCheckoutFromSignUp } from "@/features/auth/api/session";
import { SETTINGS_TABS } from "@/pages/settings-tabs";
import { lazyPage } from "./lazy-page";

/**
 * Os endereços que as configurações TINHAM, cada um levando à aba que ficou com
 * o assunto dele.
 *
 * Gerados da mesma tabela que desenha as abas (`pages/settings-tabs.ts`), e não
 * escritos à mão: à mão, um deles apontaria um dia para uma aba renomeada, e o
 * sintoma seria um link salvo abrindo a página na aba errada — coisa que ninguém
 * reporta e ninguém liga a um commit.
 *
 * A query VIAJA JUNTO, e isso não é detalhe: `/usage?turno=<id>` é o link que a
 * avaliação de um turno espalha pelo inbox e pelo chat dos agentes. Um redirect
 * declarado como objeto (`{ path, query: { aba } }`) SUBSTITUI a query, e esses
 * links passariam a abrir a lista de turnos sem turno nenhum — a regressão mais
 * fácil de cometer nesta mudança inteira.
 */
const settingsRedirects: RouteRecordRaw[] = SETTINGS_TABS.flatMap((tab) =>
	tab.from.map((path) => ({
		path,
		redirect: (to) => ({ path: "/settings", query: { ...to.query, aba: tab.value } }),
	})),
);

/**
 * Real routes, at last.
 *
 * Until now navigation was a string `ref`, which cost three things every day:
 * nothing was linkable, the back button did nothing, and a reload always landed
 * on the status page — so anyone who refreshed while reading a conversation lost
 * their place. None of that is dramatic on its own and together they are most of
 * what made the panel feel like a demo.
 *
 * Every page is loaded LAZILY, through `lazyPage`. The shell plus one screen is
 * what someone waits for; the other sixty can arrive when they are asked for, and
 * one of them pulls in mermaid.
 *
 * `lazyPage` and não `() => import(...)` cru: com o import cru o router ESPERA o
 * chunk antes de comitar, e a tela anterior fica no ecrã a fingir que o clique
 * não aconteceu. O helper faz a navegação comitar já e desenhar um esqueleto
 * enquanto o código chega — e dá uma tela nomeada ao chunk que não chega, em vez
 * de uma navegação abortada em silêncio. O porquê inteiro está lá.
 */
export const routes: RouteRecordRaw[] = [
	// -------------------------------------------------------------------------
	// Public. No session, and deliberately declared first so the guard below can
	// let them through without knowing anything about authentication.
	// -------------------------------------------------------------------------
	{
		path: "/f/:slug",
		name: "public-form",
		component: lazyPage(() => import("@/pages/PublicFormPage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	{
		// The SAME form, drawn for a host page's iframe: no card, no outer padding,
		// and `postMessage` on success. One page and one flag — V2 kept two files
		// that were almost the same, and they drifted.
		path: "/embed/f/:slug",
		name: "public-form-embedded",
		component: lazyPage(() => import("@/pages/PublicFormPage.vue")),
		props: (route) => ({ slug: route.params.slug, embedded: true }),
		meta: { public: true, bare: true },
	},
	{
		path: "/c/:slug",
		name: "public-chat",
		component: lazyPage(() => import("@/pages/PublicChatPage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	{
		/**
		 * UM registro, para UMA pessoa. As outras três rotas públicas acima são
		 * coletivas — um endereço entregue a todo mundo; esta é endereçada, e o que
		 * a autoriza é o token e não o slug.
		 *
		 * `/d/` e não `/doc/`: o produto não sabe o que o registro é, e um caminho
		 * chamado "doc" ensinaria a plataforma uma palavra que é do usuário.
		 */
		path: "/d/:token",
		name: "public-doc",
		component: lazyPage(() => import("@/pages/PublicDocPage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	{
		/**
		 * UM item do catálogo. Declarada ACIMA da página do site de propósito, ainda
		 * que não colidisse: são quatro segmentos e `/s/:tenantSlug/:pageSlug?` tem no
		 * máximo três, mas quem lê o arquivo encontra o caso específico antes do geral.
		 *
		 * O `i` é literal e o id é o do registro — o produto do blueprint da loja não
		 * tem slug, e aplicar blueprint é aditivo, então inventar um agora serviria
		 * apenas a lojas que ainda não existem.
		 */
		path: "/s/:tenantSlug/i/:id",
		name: "public-item",
		component: lazyPage(() => import("@/pages/PublicItemPage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	{
		// O portal do cliente: um contato do workspace entra com um código e vê o que é dele.
		path: "/p/:tenantSlug",
		name: "customer-portal",
		component: lazyPage(() => import("@/pages/PortalPage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	{
		// The workspace address opens its published HTML document.
		path: "/s/:tenantSlug",
		name: "public-site",
		component: lazyPage(() => import("@/pages/PublicSitePage.vue")),
		props: true,
		meta: { public: true, bare: true },
	},
	// Read before there is an account to read it with, so it cannot sit behind the
	// guard nor inside the shell.
	{
		path: "/terms",
		name: "terms",
		component: lazyPage(() => import("@/pages/TermsPage.vue")),
		meta: { public: true, bare: true },
	},
	{
		path: "/accept-invite",
		name: "accept-invite",
		component: lazyPage(() => import("@/pages/AcceptInvitePage.vue")),
		meta: { public: true, bare: true },
	},
	{
		path: "/sign-in",
		name: "sign-in",
		component: lazyPage(() => import("@/pages/SignInPage.vue")),
		meta: { public: true, bare: true },
	},
	// `/login` é o endereço que as pessoas DIGITAM (e o que a V2 usava): cai no
	// login de verdade, com a query junto, em vez de "esse endereço não existe".
	{ path: "/login", redirect: (to) => ({ name: "sign-in", query: to.query }) },

	// -------------------------------------------------------------------------
	// O PORTAL DO PARCEIRO — outro mundo, e não outra área deste.
	//
	// `partner: true` na meta é o que faz `router.beforeEach` DESVIAR antes de
	// tocar em `restoreSession()`: uma sessão de tenant nunca é restaurada numa
	// rota de parceiro, e a de parceiro não abre nenhuma rota do painel. A parede
	// é estrutural — o token do portal vive sob outro prefixo no Redis e a sessão
	// dele não tem `tenantId` nenhum —, e isto aqui é a porta dela no navegador.
	//
	// Todas `bare`: a moldura do painel tem menu, paleta e doca do agente, e nada
	// disso é do parceiro.
	// -------------------------------------------------------------------------
	{
		path: "/parceiro/entrar",
		name: "partner-sign-in",
		component: lazyPage(() => import("@/pages/PartnerSignInPage.vue")),
		meta: { public: true, bare: true, partner: true },
	},
	{
		/**
		 * O ENDEREÇO IMPRESSO NO E-MAIL. `sendPasswordLink` monta
		 * `/parceiro/senha?token=…` desde o dia um — para o convite do afiliado
		 * recém-criado E para o "esqueci minha senha" do portal, a mesma função —,
		 * e essa rota não existia: os dois links caíam no catch-all e o parceiro
		 * era largado no login do painel com `?motivo=rota-desconhecida`. Um
		 * afiliado convidado não tinha NENHUM caminho para entrar.
		 *
		 * A mesma tela e não um redirect: `PartnerSignInPage` já serve os três
		 * momentos pelo `?token=` na URL, e o endereço que foi impresso em e-mails
		 * já enviados continua sendo o que a barra mostra.
		 */
		path: "/parceiro/senha",
		name: "partner-set-password",
		component: lazyPage(() => import("@/pages/PartnerSignInPage.vue")),
		meta: { public: true, bare: true, partner: true },
	},
	{
		path: "/parceiro",
		name: "partner-home",
		component: lazyPage(() => import("@/pages/PartnerHomePage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/indicados",
		name: "partner-referrals",
		component: lazyPage(() => import("@/pages/PartnerReferralsPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/planos",
		name: "partner-plans",
		component: lazyPage(() => import("@/pages/PartnerPlansPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/faturas",
		name: "partner-invoices",
		component: lazyPage(() => import("@/pages/PartnerInvoicesPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/comissoes",
		name: "partner-commissions",
		component: lazyPage(() => import("@/pages/PartnerCommissionsPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/revendedores",
		name: "partner-resellers",
		component: lazyPage(() => import("@/pages/PartnerResellersPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/blueprints",
		name: "partner-blueprints",
		component: lazyPage(() => import("@/pages/PartnerBlueprintsPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		path: "/parceiro/conta",
		name: "partner-account",
		component: lazyPage(() => import("@/pages/PartnerAccountPage.vue")),
		meta: { bare: true, partner: true },
	},
	{
		/**
		 * O endereço impresso nos links do portal (`/cadastro?ref=…&plano=…`) —
		 * que o servidor monta desde o dia um — não tinha rota NENHUMA: caía no
		 * catch-all. Um redirect e não uma página, porque a tela é a de sign-up;
		 * o que existe aqui é a tradução dos parâmetros para o `?codigo=` que ela
		 * lê: `ref` puro passa direto (pode já vir composto, `DEMO-odonto`), e
		 * `plano` vira o sufixo `REF-plano` que o servidor sabe partir.
		 */
		path: "/cadastro",
		redirect: (to) => {
			const first = (v: unknown) => (Array.isArray(v) ? v[0] : v) as string | undefined;
			const ref = first(to.query.ref) ?? first(to.query.codigo) ?? first(to.query.code);
			const plano = first(to.query.plano);
			const codigo = ref && plano ? `${ref}-${plano}` : ref;
			return { name: "sign-up", query: codigo ? { codigo } : {} };
		},
	},
	{
		path: "/sign-up",
		name: "sign-up",
		component: lazyPage(() => import("@/pages/SignUpPage.vue")),
		meta: { public: true, bare: true },
	},
	// Forgetting a password is the one thing you do while locked out, so the screen
	// is public and bare like sign-in — the shell it would otherwise sit in needs a
	// session to render its nav.
	{
		path: "/reset-password",
		name: "reset-password",
		component: lazyPage(() => import("@/pages/ResetPasswordPage.vue")),
		meta: { public: true, bare: true },
	},
	// Opened from the inbox of the NEW address, maybe on a phone with no session.
	{
		path: "/confirm-email",
		name: "confirm-email",
		component: lazyPage(() => import("@/pages/ConfirmEmailPage.vue")),
		meta: { public: true, bare: true },
	},

	// -------------------------------------------------------------------------
	// The panel. Everything below needs a session.
	// -------------------------------------------------------------------------
	// Landing on the status page meant a new workspace opened on dependency
	// checks and a list of architecture layers — accurate, and an answer to a
	// question nobody signing up has.
	{ path: "/", name: "home", component: lazyPage(() => import("@/pages/HomePage.vue")) },
	/**
	 * O primeiro dia — e a única tela do painel que um workspace vazio precisa.
	 *
	 * Sem `minRole`, e de propósito: pesquisar a própria empresa e ler o dossiê é de
	 * qualquer sessão. O portão está onde a estrutura é criada — `POST
	 * /v3/blueprints/import` é `@MinRole('admin')` — e a tela esconde exatamente esse
	 * botão para quem não é. Trancar a rota inteira deixaria um membro sem nunca ver
	 * o que a proposta diz.
	 *
	 * `under: "/"` e nenhuma linha na lateral: é o passo 1 da home, e chega-se aqui
	 * por ele. Uma linha permanente no menu para uma tela que se encerra no primeiro
	 * dia é a decoração que a própria home evita ao se esvaziar.
	 */
	{
		path: "/comecar",
		name: "onboarding",
		component: lazyPage(() => import("@/pages/OnboardingPage.vue")),
		meta: { under: "/" },
	},
	/** O segundo passo do primeiro dia: o chat, ligado ao funil, e a primeira conversa. */
	{
		path: "/comecar/chat",
		name: "onboarding-chat",
		component: lazyPage(() => import("@/pages/OnboardingChatPage.vue")),
		meta: { under: "/" },
	},
	/** O fim do primeiro dia: o que foi montado, com os endereços (23/10). */
	{
		path: "/comecar/pronto",
		name: "onboarding-done",
		component: lazyPage(() => import("@/pages/OnboardingDonePage.vue")),
		meta: { under: "/" },
	},
	/**
	 * TUDO que se configura, numa página.
	 *
	 * Sem `minRole` na meta, e de propósito: cinco das seis abas são de qualquer
	 * sessão — trocar a própria senha, ver por onde os contatos chegam, escolher o
	 * tema. Só a aba Equipe é de administrador, e quem a esconde é a própria tabela
	 * de abas, que é também quem a mostra. Um portão na rota inteira devolveria a
	 * senha de um atendente para debaixo de um papel que ele não tem.
	 *
	 * Os endereços que estas abas tinham antes continuam valendo, logo abaixo.
	 */
	{
		path: "/settings",
		name: "settings",
		component: lazyPage(() => import("@/pages/SettingsPage.vue")),
	},
	...settingsRedirects,

	// -------------------------------------------------------------------------
	// BILLING — cinco seções e uma fatura, e não uma página de 1.370 linhas.
	//
	// A V2 empilhava plano, ciclo, créditos, pacotes, limites, adicionais,
	// faturas, consumo e cancelamento numa rolagem só. Nenhum controle se perdeu;
	// o que cada assunto ganhou foi um ENDEREÇO — e um endereço pode ser mandado a
	// um colega ("olha a fatura de março") e guardado nos favoritos de quem paga.
	//
	// Endereços em INGLÊS desde o plano modular (§10.1): as URLs antigas em
	// português redirecionam logo abaixo, como FUNÇÃO, para a query viajar junto.
	//
	// `minRole: "admin"` porque quem paga é quem administra. `meta.under` diz de
	// que tela cada seção é uma seção: a lateral tem UMA linha de Cobrança, e é o
	// `nav-items.spec` que cobra que nenhuma delas fique inalcançável.
	// -------------------------------------------------------------------------
	{
		path: "/billing",
		name: "billing",
		component: lazyPage(() => import("@/pages/BillingPage.vue")),
		meta: { minRole: "admin" },
	},
	{
		path: "/billing/credits",
		name: "billing-credits",
		component: lazyPage(() => import("@/pages/BillingCreditsPage.vue")),
		meta: { minRole: "admin", under: "/billing" },
	},
	{
		path: "/billing/limits",
		name: "billing-limits",
		component: lazyPage(() => import("@/pages/BillingLimitsPage.vue")),
		meta: { minRole: "admin", under: "/billing" },
	},
	{
		path: "/billing/invoices",
		name: "billing-invoices",
		component: lazyPage(() => import("@/pages/BillingInvoicesPage.vue")),
		meta: { minRole: "admin", under: "/billing" },
	},
	{
		// O mês no endereço, `YYYY-MM`, que é o que o servidor usa como referência —
		// e não o id da fatura: "a de março" é o que a pessoa tem na mão.
		path: "/billing/invoices/:month",
		name: "billing-invoice",
		component: lazyPage(() => import("@/pages/BillingInvoicePage.vue")),
		props: true,
		meta: { minRole: "admin", under: "/billing" },
	},
	{
		path: "/billing/consumption",
		name: "billing-consumption",
		component: lazyPage(() => import("@/pages/BillingConsumptionPage.vue")),
		meta: { minRole: "admin", under: "/billing" },
	},
	// Os endereços antigos, em português. FUNÇÕES, não objetos: um redirect
	// declarado como objeto substituiria a query, e a query viaja junto.
	// Also Stripe's return address: a checkout that left from the sign-up goes
	// back to the onboarding instead (see `markCheckoutFromSignUp`).
	{
		path: "/cobranca",
		redirect: (to) =>
			takeCheckoutFromSignUp() ? "/comecar" : { path: "/billing", query: to.query },
	},
	{
		path: "/cobranca/creditos",
		redirect: (to) => ({ path: "/billing/credits", query: to.query }),
	},
	{ path: "/cobranca/limites", redirect: (to) => ({ path: "/billing/limits", query: to.query }) },
	{ path: "/cobranca/faturas", redirect: (to) => ({ path: "/billing/invoices", query: to.query }) },
	{
		path: "/cobranca/faturas/:mes",
		redirect: (to) => ({ path: `/billing/invoices/${to.params.mes}`, query: to.query }),
	},
	{
		path: "/cobranca/consumo",
		redirect: (to) => ({ path: "/billing/consumption", query: to.query }),
	},
	// `/creditos` e `/faturas` nunca existiram sem o prefixo, mas o plano (§10.1)
	// fixa os destinos curtos também — quem digitar de cabeça chega.
	{ path: "/creditos", redirect: (to) => ({ path: "/billing/credits", query: to.query }) },
	{ path: "/faturas", redirect: (to) => ({ path: "/billing/invoices", query: to.query }) },
	{
		/**
		 * A parede do primeiro acesso — e ela é uma PORTA.
		 *
		 * A pergunta única do plano modular (M1): quatro respostas, cada uma um
		 * preset com o preço visível ANTES do clique.
		 *
		 * ── ELA DEIXOU DE SER `bare` (25/08/2026) ─────────────────────────────
		 *
		 * Era `bare` com o argumento de que "é uma escolha, não uma tela do painel",
		 * e o argumento estava errado pelo lado de quem lê: sem cabeçalho e sem menu
		 * a página não parecia do produto — parecia um checkout de terceiro pedindo
		 * cartão. Quem chega aqui JÁ ENTROU (tem sessão, tem workspace); o que falta
		 * é plano. Dar-lhe a casa inteira é dizer o que ele está comprando.
		 *
		 * `under: "/billing"` porque `nav-items.spec` exige que toda tela do painel
		 * tenha dono — sem linha própria no menu, escolher plano é seção de
		 * Cobrança, que é de onde se chega aqui. Sem `minRole` e sem guarda de
		 * plano: não existe `PlanGuard` global nesta aplicação, e chega-se aqui
		 * porque `GET /v3/billing/me` respondeu `plan: null` e alguma tela ofereceu
		 * o caminho. Quem digitar `/` continua entrando no painel.
		 */
		path: "/subscribe",
		name: "subscribe",
		component: lazyPage(() => import("@/pages/SubscribePage.vue")),
		meta: { under: "/billing" },
	},
	{ path: "/escolher-plano", redirect: (to) => ({ path: "/subscribe", query: to.query }) },
	// The DEV routes. `platformAdmin` on the meta, not just hidden from the nav:
	// hiding a link is presentation, and someone can always type the address.
	//
	// Elas ficaram FORA de `/settings` de propósito. Uma tela chamada
	// "Configurações" dentro do workspace de alguém promete que o que se edita ali
	// é dele, e "Instruções do sistema" alcança todos os workspaces de uma vez —
	// a mesma razão pela qual `nav-items.ts` a mantém no grupo Dev.
	{
		path: "/prompts",
		name: "prompts",
		component: lazyPage(() => import("@/pages/PromptsPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		path: "/platform",
		name: "platform",
		component: lazyPage(() => import("@/pages/PlatformPage.vue")),
		meta: { platformAdmin: true },
	},
	// -------------------------------------------------------------------------
	// O DINHEIRO DA INSTALAÇÃO — cobrança, catálogo e parceiros.
	//
	// Declaradas ANTES de `/platform/:id` de propósito. O ranking do vue-router já
	// prefere um segmento estático a um parâmetro, então `/platform/cobranca` não
	// cairia no dossiê de um workspace chamado "cobranca" de todo jeito — mas quem
	// lê o arquivo encontra o caso específico antes do geral, e a regra de leitura
	// vale mais do que a regra de desempate.
	// -------------------------------------------------------------------------
	{
		// Qual IA responde cada função, em ordem de fallback — editável em runtime.
		// Estática ANTES de `/platform/:id`, como as demais desta seção.
		path: "/platform/models",
		name: "platform-models",
		component: lazyPage(() => import("@/pages/PlatformModelsPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// O endereço em português. Uma FUNÇÃO, para a query viajar junto.
		path: "/platform/modelos",
		redirect: (to) => ({ path: "/platform/models", query: to.query }),
	},
	{
		// COM QUE VOZ o atendente fala — provedor, e a voz padrão de cada sexo.
		// Estática ANTES de `/platform/:id`, como as demais desta seção.
		path: "/platform/voices",
		name: "platform-voices",
		component: lazyPage(() => import("@/pages/PlatformVoicesPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// O endereço em português. Uma FUNÇÃO, para a query viajar junto — mesma
		// razão de `/platform/modelos` acima.
		path: "/platform/vozes",
		redirect: (to) => ({ path: "/platform/voices", query: to.query }),
	},
	{
		// QUANTO CADA FERRAMENTA CUSTA em créditos (0145). Ao lado de `/platform/models`
		// e `/platform/voices` porque é a mesma família — o que a IA cobra —, e tela
		// própria porque responde a outra pergunta: aquelas dizem QUEM serve, esta diz
		// o que a chamada custa a quem paga.
		path: "/platform/tool-costs",
		name: "platform-tool-costs",
		component: lazyPage(() => import("@/pages/PlatformToolCostsPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// O endereço em português, como os vizinhos. Uma FUNÇÃO, para a query viajar.
		path: "/platform/ferramentas",
		redirect: (to) => ({ path: "/platform/tool-costs", query: to.query }),
	},
	{
		// O CONSUMO. Estática ANTES de `/platform/:id`, como as demais desta seção.
		// Ganhou endereço próprio em 25/08/2026: vivia como uma seção no meio de
		// `/platform`, sem item de menu, e não se manda o link de uma seção.
		path: "/platform/usage",
		name: "platform-usage",
		component: lazyPage(() => import("@/pages/PlatformUsagePage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// O endereço em português. Uma FUNÇÃO, para a query viajar junto — mesma
		// razão de `/platform/modelos` logo acima.
		path: "/platform/consumo",
		redirect: (to) => ({ path: "/platform/usage", query: to.query }),
	},
	{
		path: "/platform/billing",
		name: "platform-billing",
		component: lazyPage(() => import("@/pages/PlatformBillingPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// The old Portuguese address. A FUNCTION, so the query travels along.
		path: "/platform/cobranca",
		redirect: (to) => ({ path: "/platform/billing", query: to.query }),
	},
	{
		// A prateleira DO SISTEMA (0347): a mesma tela do portal, com a porta do Dev.
		path: "/platform/system-blueprints",
		name: "platform-system-blueprints",
		component: lazyPage(() => import("@/pages/PartnerBlueprintsPage.vue")),
		props: { scope: "system" },
		meta: { platformAdmin: true },
	},
	{
		path: "/platform/catalog",
		name: "platform-catalog",
		component: lazyPage(() => import("@/pages/PlatformCatalogPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// The old Portuguese address. A FUNCTION, not a string, so the query
		// travels along — a redirect declared as an object would replace it.
		path: "/platform/catalogo",
		redirect: (to) => ({ path: "/platform/catalog", query: to.query }),
	},
	{
		// A VITRINE do primeiro dia, editável (0195). Ao lado do catálogo de planos
		// porque é a outra metade do que se oferece a quem chega — ali o preço,
		// aqui o que o workspace ganha —, e com endereço próprio porque
		// `/platform/catalog` já é dos planos.
		path: "/platform/showcase",
		name: "platform-showcase",
		component: lazyPage(() => import("@/pages/PlatformShowcasePage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// O endereço em português. Uma FUNÇÃO, para a query viajar junto — mesma
		// razão de `/platform/modelos` acima.
		path: "/platform/vitrine",
		redirect: (to) => ({ path: "/platform/showcase", query: to.query }),
	},
	{
		path: "/platform/parceiros",
		name: "platform-partners",
		component: lazyPage(() => import("@/pages/PlatformPartnersPage.vue")),
		meta: { platformAdmin: true },
	},
	{
		// ANTES da rota com `:id`, e isto aqui não é estilo: "comissoes" é um
		// segmento estático que um id nunca vai ser, e a ordem torna isso visível.
		path: "/platform/parceiros/comissoes",
		name: "platform-partner-commissions",
		component: lazyPage(() => import("@/pages/PlatformPartnerCommissionsPage.vue")),
		meta: { platformAdmin: true, under: "/platform/parceiros" },
	},
	{
		path: "/platform/parceiros/:id",
		name: "platform-partner",
		component: lazyPage(() => import("@/pages/PlatformPartnerPage.vue")),
		props: true,
		meta: { platformAdmin: true },
	},
	{
		// O dossiê de UM workspace. Pelo id e não pelo slug: a tabela já carrega o
		// id, e resolver slug→id no servidor seria uma consulta a mais para chegar
		// exatamente onde o link já estava.
		//
		// `platformAdmin` na meta como a rota acima, e não só escondendo o link:
		// esconder é apresentação, e endereço qualquer um digita.
		path: "/platform/:id",
		name: "platform-tenant",
		component: lazyPage(() => import("@/pages/PlatformTenantPage.vue")),
		props: true,
		meta: { platformAdmin: true },
	},
	// O agente aberto mora NO ENDEREÇO (`/agents/luana`): o Voltar do navegador
	// volta à galeria em vez de sair da tela, e um atendente pode ser mandado por link.
	{
		path: "/agents/:code?",
		name: "agents",
		component: lazyPage(() => import("@/pages/AgentsPage.vue")),
		props: true,
	},
	// As perguntas prontas que o assistente oferece em cada tela (migração 0124).
	// Ao lado de `/agents` porque é a mesma pergunta em outra hora: lá se configura
	// QUEM responde, aqui o que ele oferece antes de alguém escrever.
	{
		path: "/suggestions",
		name: "suggestions",
		component: lazyPage(() => import("@/pages/SuggestionsPage.vue")),
		meta: { minRole: "admin" },
	},
	// The conversation is IN the address, so it can be sent to a colleague and
	// survives a reload. Optional because `/inbox` on its own is the list, which is
	// what every link in the shell points at.
	{
		path: "/inbox/:id?",
		name: "inbox",
		component: lazyPage(() => import("@/pages/InboxPage.vue")),
	},
	{
		path: "/missions",
		name: "missions",
		component: lazyPage(() => import("@/pages/MissionsPage.vue")),
	},
	{ path: "/help", name: "help", component: lazyPage(() => import("@/pages/HelpPage.vue")) },
	// O manual do produto. Nada a ver com `/help`, que é a fila de perguntas que os
	// AGENTES fizeram a uma pessoa — dois destinos com nomes parecidos e assuntos
	// opostos. Sem `minRole`: ler como o painel funciona é justamente o que quem
	// acabou de chegar precisa. O capítulo vai no endereço para poder ser mandado a
	// um colega.
	{
		path: "/manual/:slug?",
		name: "manual",
		component: lazyPage(() => import("@/pages/ManualPage.vue")),
	},
	{
		path: "/contacts",
		name: "contacts",
		component: lazyPage(() => import("@/pages/ContactsPage.vue")),
	},
	// The person, as a screen. No `minRole`: a ficha is reading, and an operator who
	// answers her is exactly who needs it.
	{
		path: "/contacts/:id",
		name: "contact",
		component: lazyPage(() => import("@/pages/ContactPage.vue")),
		props: true,
	},
	/**
	 * `under: "/funnels"` e nenhuma linha na lateral — o kanban é uma SEÇÃO de Funis.
	 *
	 * A palavra "Funis" chegou a estar três vezes no menu: aqui (o kanban), em
	 * "Meus funis" (a lista) e no cabeçalho do grupo dinâmico. E a divisão era
	 * arbitrária — abrir um funil era Trabalho, criá-lo era Construir, e a mesma
	 * pessoa fazia as duas coisas no mesmo minuto. Agora `/funnels` é a tela de
	 * funis inteira (abre, cria, edita, arquiva) e o kanban é para onde ela leva.
	 *
	 * `meta.under` e não simplesmente sumir da tabela: `nav-items.spec` prova que
	 * nenhuma tela do painel ficou órfã, e uma seção declara de quem é seção —
	 * um `under` apontando para endereço fora do menu volta a acusar órfã.
	 */
	{
		path: "/board",
		name: "board",
		component: lazyPage(() => import("@/pages/BoardPage.vue")),
		meta: { under: "/funnels" },
		/**
		 * `?painel=config` já não abre nada no quadro — ele MUDA de tela.
		 *
		 * O quadro tinha um painel de abas próprio para configurar o funil, com as
		 * mesmas cinco abas de `/funnels/:code` e menos coisa dentro: sem os fluxos
		 * e as automações de cada etapa, sem apagar, sem reordenar arrastando. Duas
		 * telas quase iguais, uma delas incompleta, é confusão pura; a que ficou é a
		 * de verdade, e os links guardados (a lista de funis mandava para cá com
		 * `?painel=config&aba=entradas`) continuam a chegar onde queriam chegar.
		 *
		 * Guarda de rota e NÃO um `if` no `setup` da página: o quadro escreve o
		 * próprio endereço num `router.replace` diferido (`use-board-address`), e
		 * essa escrita corria por cima do redirecionamento no mesmo tique — o
		 * sintoma era o link antigo ficar parado no quadro. Aqui a troca acontece
		 * antes de a página existir.
		 */
		beforeEnter: (to) => {
			if (to.query.painel !== "config") return true;
			const funnel = typeof to.query.funil === "string" ? to.query.funil : "";
			if (!funnel) return { path: "/funnels" };
			const aba = typeof to.query.aba === "string" ? to.query.aba : "";
			return {
				path: `/funnels/${encodeURIComponent(funnel)}`,
				query: aba ? { aba } : {},
			};
		},
	},
	// `minRole` on the screens that only CONFIGURE the workspace. Not on `/agents`:
	// a member has to see the agents, and the lock there is per action, not per
	// screen.
	//
	// `/channels` saiu dessa lista. Sem `minRole`, e de propósito: quem percebe primeiro que um canal caiu é quem
	// atende — um cliente disse que ninguém respondeu. Toda a LEITURA desta tela já é
	// aberta no servidor (`GET /v3/channels`, `kinds`, `:id`, `:id/health`,
	// `:id/templates`, `:id/profile`); só a escrita é `@MinRole('admin')`, e a tela
	// esconde exatamente esse conjunto por `useCan("admin")`.
	//
	// Continua sendo um DESTINO, e não só a aba `?aba=canais`: conectar um número é
	// configuração, e descobrir às nove da manhã que ele caiu é trabalho. É a única
	// tela do produto que responde às duas perguntas, e o caminho até a segunda tem
	// de ser um clique.
	{
		path: "/channels",
		name: "channels",
		component: lazyPage(() => import("@/pages/ChannelsPage.vue")),
	},
	{
		/**
		 * Where Instagram sends the person back. The path is not a choice: it is what
		 * `INSTAGRAM_REDIRECT_URI` was configured as and what Meta has registered, and
		 * it had no route — so authorising landed on a 404 with the code in the URL.
		 *
		 * `bare` because it is a transitional page: showing the app shell around a
		 * three-second exchange invites a click that navigates away mid-flight.
		 */
		path: "/channels/instagram/callback",
		name: "instagram-callback",
		component: lazyPage(() => import("@/pages/InstagramCallbackPage.vue")),
		meta: { bare: true },
	},
	{
		// O link de suporte: token no caminho, troca na página, home do workspace.
		path: "/suporte/:token",
		name: "support-access",
		component: lazyPage(() => import("@/pages/SupportAccessPage.vue")),
		// `public`: quem chega pelo link NÃO tem sessão — é o link que a cria.
		meta: { bare: true, public: true },
	},
	{
		/**
		 * Onde a Meta devolve quem foi pelo OAuth de página inteira — a alternativa ao
		 * Embedded Signup, para popup bloqueado ou app em modo de desenvolvimento.
		 *
		 * O caminho não é uma escolha: é o valor de `META_REDIRECT_URI` e o que está
		 * cadastrado no painel da Meta. Sem esta rota, autorizar cai num 404 com o código
		 * na URL — exatamente o que acontecia com o do Instagram antes de ele ganhar a
		 * dele.
		 *
		 * `bare` porque é uma página de passagem: ela guarda o código e volta para
		 * `/channels` no mesmo tique, e a casca do app em volta convida a um clique que
		 * navega para fora no meio do caminho.
		 */
		path: "/channels/meta/callback",
		name: "meta-callback",
		component: lazyPage(() => import("@/pages/MetaCallbackPage.vue")),
		meta: { bare: true },
	},
	{
		path: "/automation",
		name: "automation",
		component: lazyPage(() => import("@/pages/AutomationPage.vue")),
		meta: { minRole: "admin" },
	},
	/**
	 * As rotas de uma automação: escrever e abrir.
	 *
	 * `new` ANTES de `:id` — a ordem importa, porque `/automation/new` casaria com
	 * `/automation/:id` e a página de detalhe iria buscar uma regra chamada "new".
	 *
	 * Cada uma tem endereço próprio porque cada uma é uma coisa que se manda a um
	 * colega: "cria uma automação para isto", "olha esta automação". Ler e ajustar
	 * eram duas telas; hoje abrir JÁ é ajustar (o desenho abre de cara).
	 */
	{
		path: "/automation/new",
		name: "automation-new",
		component: lazyPage(() => import("@/pages/AutomationNewPage.vue")),
		// Sem linha própria no menu: chega-se a elas pela lista, e `under` é como
		// uma tela declara de quem é seção — ver `nav-items.spec.ts`.
		meta: { minRole: "admin", under: "/automation" },
	},
	{
		path: "/automation/:id",
		name: "automation-rule",
		component: lazyPage(() => import("@/pages/AutomationRulePage.vue")),
		// Sem linha própria no menu: chega-se a elas pela lista, e `under` é como
		// uma tela declara de quem é seção — ver `nav-items.spec.ts`.
		meta: { minRole: "admin", under: "/automation" },
	},
	{
		// A antiga tela de ajuste: um link guardado cai na tela única, no desenho.
		path: "/automation/:id/edit",
		redirect: (to) => `/automation/${encodeURIComponent(String(to.params.id))}`,
	},
	{
		/**
		 * Os painéis, e UM painel — a mesma tela em dois estados, como `/inbox` e
		 * `/inbox/:id`. Uma letra pelo mesmo motivo de `/e/` e `/f/`: o endereço é
		 * curto porque é feito para ser mandado a um colega.
		 *
		 * O parâmetro é opcional de propósito: com ele obrigatório, "quais painéis
		 * existem" precisaria de uma segunda rota, uma segunda página e um segundo
		 * item de menu para uma pergunta que é a mesma.
		 */
		path: "/r/:code?",
		name: "report",
		component: lazyPage(() => import("@/pages/ReportPage.vue")),
		props: true,
	},
	{ path: "/memory", name: "memory", component: lazyPage(() => import("@/pages/MemoryPage.vue")) },
	// Sem `minRole`: quem abre é quem tem o módulo `site` (a guarda lê `moduleForPath`).
	{ path: "/site", name: "site", component: lazyPage(() => import("@/pages/SiteBuilderPage.vue")) },
	{
		/**
		 * A ESTRUTURA VIROU A ÁRVORE DE "SEU SISTEMA" (18/10/2026): as telas, o que é
		 * delas e quem as usa, com o desfazer ao lado. O endereço antigo continua a
		 * valer; `?status=archived` abre as arquivadas.
		 */
		path: "/structure",
		redirect: (to) => ({
			path: "/system",
			query: to.query.status === "archived" ? { no: "archived" } : {},
		}),
	},
	{
		/**
		 * SEU SISTEMA: o que o workspace tem, o que pode ter e cada mudança que
		 * já fizeram nele — com o caminho de volta.
		 *
		 * `admin` como `/blueprints` era, e pela mesma razão do servidor: o
		 * resumo, a trilha e o inventário são todos `@MinRole("admin")`, e uma
		 * tela que carrega três consultas proibidas é uma tela de três erros.
		 */
		path: "/system",
		name: "system",
		component: lazyPage(() => import("@/pages/SystemPage.vue")),
		meta: { minRole: "admin" },
	},
	{
		/**
		 * O AVANÇADO tem endereço próprio e não é uma quarta aba: é outro
		 * trabalho — editar um documento, reverter uma versão — e um endereço
		 * próprio é o que deixa mandá-lo a um colega.
		 */
		path: "/system/advanced",
		name: "system-advanced",
		component: lazyPage(() => import("@/pages/SystemAdvancedPage.vue")),
		// Sem linha própria no menu: chega-se a ela por "Seu sistema", e `under`
		// é como uma tela declara de quem é seção — ver `nav-items.spec.ts`.
		meta: { minRole: "admin", under: "/system" },
	},
	{
		/** A vitrine passou a ser uma aba de "Seu sistema"; o endereço continua a valer. */
		path: "/blueprints",
		redirect: "/system",
	},
	{ path: "/files", name: "files", component: lazyPage(() => import("@/pages/FilesPage.vue")) },
	{
		/**
		 * TODOS os funis, lado a lado — a pergunta que o quadro não responde.
		 *
		 * `/board` abre UM funil; comparar cinco custava abrir cinco, e achar um funil
		 * arquivado exigia ter um funil aberto. Endereço próprio e não aba de
		 * `/settings`: um funil é TRABALHO, e o que se faz aqui — arquivar, restaurar,
		 * definir o padrão — é sobre o trabalho de hoje, não sobre a casa.
		 *
		 * ── SEM `minRole`, COMO `/channels` ───────────────────────────────────
		 *
		 * Ela era `minRole: "admin"`, e isso passou a custar caro quando `/board`
		 * deixou de ter linha no menu: esta virou a ÚNICA porta para o funil, e
		 * trancá-la inteira tirava do atendente o quadro que ele usa o dia todo.
		 *
		 * A tela sempre soube ser lida por quem não administra — `canManage` já
		 * escondia "Novo funil" e cada item mutante do menu de cada linha. O que
		 * faltava era o outro lado: `BoardController` não tinha UM `@MinRole`, de
		 * modo que a tranca existia só aqui e quem soubesse a URL da API apagava
		 * etapa sendo atendente. Agora as seis rotas que CONFIGURAM o funil (criar
		 * pipeline, renomear, criar/editar/apagar/reordenar etapa) pedem admin no
		 * servidor, e as de card não pedem nada — que é o trabalho do dia.
		 *
		 * Ler para todos, configurar para admin: a mesma divisão de `/channels`, e
		 * agora com a metade que faltava.
		 */
		path: "/funnels",
		name: "funnels",
		component: lazyPage(() => import("@/pages/FunnelsPage.vue")),
	},
	// O endereço em português. FUNÇÃO e não objeto, para a query (`?grupo=`) viajar junto.
	{ path: "/funis", redirect: (to) => ({ path: "/funnels", query: to.query }) },
	{
		/**
		 * UM FUNIL, CONFIGURADO — o hub para onde a lista passou a levar.
		 *
		 * Clicar num funil abria o kanban. Isso serve quem vai TRABALHAR e não quem
		 * vai CONSTRUIR — e quem constrói chegava ao quadro, procurava "Configurar" e
		 * só então via as etapas. O submenu da lateral já dá um clique por funil
		 * direto para `/board?funil=`, então a lista ficou livre para levar ao lugar
		 * onde o funil se desenha, e o hub tem "Abrir o quadro" no cabeçalho.
		 *
		 * `meta.under` como o kanban: é uma SEÇÃO de Funis, sem linha própria na
		 * lateral. O parâmetro obrigatório já a tira da conta de telas órfãs
		 * (`nav-items.spec`), e a declaração continua a valer se ele um dia sair.
		 *
		 * Sem `minRole`, como `/funnels`: LER a configuração de um funil é de
		 * qualquer sessão, e as seis rotas que a ESCREVEM pedem admin no servidor. A
		 * tela esconde exatamente esse conjunto por `useCan("admin")`.
		 */
		path: "/funnels/:code",
		name: "funnel-config",
		component: lazyPage(() => import("@/pages/FunnelConfigPage.vue")),
		meta: { under: "/funnels" },
	},
	{
		// The whole reason deep links matter: a screen someone lives in all day
		// should be a URL they can send to a colleague.
		path: "/e/:entityCode",
		name: "entity",
		component: lazyPage(() => import("@/pages/EntityPage.vue")),
		props: true,
	},
	{
		/**
		 * UMA tela do agente, no seu próprio endereço.
		 *
		 * Curto de propósito (`/t/` e não `/e/:code/tela/:view`): é um lugar onde
		 * uma equipe passa o dia e um link que se manda para um colega, não um
		 * detalhe da entidade. E é isso mesmo que a separação diz — a entidade é o
		 * bastidor, a tela é o palco.
		 */
		path: "/t/:entityCode/:viewCode",
		name: "screen",
		component: lazyPage(() => import("@/pages/ScreenPage.vue")),
		props: true,
	},
	{
		/**
		 * ONE record of a dynamic entity — the view of `kind: "detail"`, which has been
		 * declarable since views existed and had no address to be drawn at.
		 *
		 * The same component as the list on purpose: `EntityPage` already resolves the
		 * entity, its views and its records from metadata, and a second page would be a
		 * second place to keep that in step. It receives `id` as a prop and renders the
		 * detail when it has one.
		 */
		path: "/e/:entityCode/:id",
		name: "entity-record",
		component: lazyPage(() => import("@/pages/EntityPage.vue")),
		props: true,
	},

	// Anything else. Not a 404 page — an unknown path in a panel is almost always
	// a stale link, and dropping someone at the start is more useful than a
	// dead end.
	// Função e não string porque o motivo viaja junto: aterrissar no começo sem
	// nada dito é indistinguível de um clique que não funcionou.
	{
		path: "/:pathMatch(.*)*",
		redirect: (to) => ({ path: "/", query: { motivo: "rota-desconhecida", de: to.fullPath } }),
	},
];
